import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MatTableDataSource, MatDialog } from '@angular/material';
import { ConfigurationSummary } from '../../../../../../../models/configuration-summary.model';
import { AddAccessGroupDialogComponent } from './add-access-group-dialog.component';
import { ConfigurationAccessGroup } from '../../../../../../../core/services/data/request/configuration-access.service';
import { ConfirmationDialogComponent } from 'src/app/modules/feature/common/confirmation-dialog/confirmation-dialog.component';
import { DrawerService } from 'src/app/core/services/helper/drawer.service';
import { AdminDataService } from 'src/app/core/services/data/data.service';
import { UserDetailData } from 'src/app/models/user-detail-data.model';
import { UserGroup } from 'src/app/models/user-group.model';
import { Subscription } from 'rxjs';

export interface GroupWithAssociations extends ConfigurationAccessGroup {
  isDefault?: boolean;
  isUnassigned?: boolean;
  canEdit?: boolean;
  userIds?: number[];
  userGroupIds?: number[];
  configurationUuids?: string[];
  expanded?: boolean;
  visible?: boolean;
  hasConfigurations?: boolean;
}

export interface AccessGroupEvent {
  action: 'add' | 'edit' | 'delete';
  result: any;
}

@Component({
  selector: 'app-configuration-card',
  templateUrl: './configuration-card.component.html',
  styleUrls: ['./configuration-card.component.scss']
})
export class ConfigurationCardComponent implements OnInit, OnChanges, OnDestroy {
  title = 'Configuration Management';

  @Input() dataSource: MatTableDataSource<ConfigurationSummary>;
  @Input() displayedColumns: string[];
  @Input() accessGroups: GroupWithAssociations[] = [];
  @Input() users: UserDetailData[] = [];
  @Input() userGroups: UserGroup[] = [];

  displayedColumnsWithActions: string[];

  @Output() accessGroupEvent = new EventEmitter<AccessGroupEvent>();
  @Output() refreshEmitter = new EventEmitter<void>();
  @Output() userClick = new EventEmitter<number>();
  @Output() userGroupClick = new EventEmitter<number>();
  @Output() configurationClick = new EventEmitter<string>();
  @Output() loadAccessGroupsRequest = new EventEmitter<void>();
  @Output() deleteAccessGroupRequest = new EventEmitter<string>();

  filteredAccessGroups: GroupWithAssociations[] = [];
  private configCache: Map<string, ConfigurationSummary> = new Map();
  private userCache: Map<number, UserDetailData> = new Map();
  private userGroupCache: Map<number, UserGroup> = new Map();
  

  private subscriptions: Subscription[] = [];

  private _isFiltering = false;
  get isFiltering(): boolean {
    return this._isFiltering;
  }
  filterValue: string = '';

  constructor(
    private dialog: MatDialog,
    private drawerService: DrawerService,
    private dataService: AdminDataService
  ) { }
  
  ngOnInit() {
    this.displayedColumnsWithActions = [...this.displayedColumns, 'actions'];   

    this.updateFilteredGroups();
    
    const configSub = this.dataService.configurationSummaries.subscribe(configs => {
      if (configs && configs.length > 0) {
        configs.forEach(config => {
          this.configCache.set(config.configurationId, config);
        });
      }
    });
    this.subscriptions.push(configSub);
    
    const userSub = this.dataService.userDetails.subscribe(users => {
      if (users && users.length > 0) {
        users.forEach(user => {
          this.userCache.set(user.userId, user);
        });
      }
    });
    this.subscriptions.push(userSub);
    
    const userGroupSub = this.dataService.userGroupDetails.subscribe(groups => {
      if (groups && groups.length > 0) {
        groups.forEach(group => {
          this.userGroupCache.set(group.id, group);
        });
      }
    });
    this.subscriptions.push(userGroupSub);
  }
  
  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Initialize the caches with any available data
    if (changes.users && this.users) {
      this.users.forEach(user => {
        this.userCache.set(user.userId, user);
      });
    }
    
    if (changes.userGroups && this.userGroups) {
      this.userGroups.forEach(group => {
        this.userGroupCache.set(group.id, group);
      });
    }
  
    if (changes.dataSource && this.dataSource && this.dataSource.data) {
      this.dataSource.data.forEach(config => {
        this.configCache.set(config.configurationId, config);
      });
    }
    
    if (changes.accessGroups && this.accessGroups) {
      // Force a detection cycle after access groups are updated
      setTimeout(() => {
        this.updateFilteredGroups();
      });
    } else {
      this.updateFilteredGroups();
    }
  }

  updateFilteredGroups() {
    if (this.accessGroups) {
      this.filteredAccessGroups = [...this.accessGroups];      
      if (this.filterValue) {
        this.applyFilterValue();
      }      
      this.updateConfigurationGroups();
    }
  }

  enableFiltering() {
    this._isFiltering = true;
  }

  disableFiltering() {
    this._isFiltering = false;
    this.filterValue = '';
    this.updateFilteredGroups();
  }

  hasConfigurations(group: GroupWithAssociations): boolean {
    return group.configurationUuids ? group.configurationUuids.length > 0 : false;
  }

  applyFilter(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.filterValue = inputValue ? inputValue.trim().toLowerCase() : '';
    this.applyFilterValue();
  }

  private applyFilterValue() {
    if (!this.filterValue) {
      this.filteredAccessGroups = [...this.accessGroups];
      return;    }
    
    this.filteredAccessGroups = this.accessGroups.filter(group => {
      if (group.name.toLowerCase().includes(this.filterValue)) {
        return true;
      }      
      if (group.configurationUuids && group.configurationUuids.some(uuid => {
        const configName = this.getConfigNameByUuid(uuid);
        return configName.toLowerCase().includes(this.filterValue);
      })) {
        return true;
      }      
      if (group.userIds && group.userIds.some(userId => {
        const userName = this.getUserNameById(userId);
        return userName.toLowerCase().includes(this.filterValue) || userId.toString().includes(this.filterValue);
      })) {
        return true;
      }      
      if (group.userGroupIds && group.userGroupIds.some(groupId => {
        const groupName = this.getUserGroupNameById(groupId);
        return groupName.toLowerCase().includes(this.filterValue) || groupId.toString().includes(this.filterValue);
      })) {
        return true;
      }
      
      return false;
    });
  }
  
  onUserClick(userId: number): void {
    this.userClick.emit(userId);
    
    const user = this.userCache.get(userId) || 
                this.users.find(u => u.userId === userId);
    
    if (user) {
      this.dataService.setCurrentSelectedUserDetails(user);      
      this.drawerService.openUserDrawer();
    }
  }
  
  onUserGroupClick(userGroupId: number): void {
    this.userGroupClick.emit(userGroupId);    
    const userGroup = this.userGroupCache.get(userGroupId) || 
                     this.userGroups.find(g => g.id === userGroupId);
    
    if (userGroup) {
      this.dataService.setCurrentSelectedUserGroupDetails(userGroup);      
      this.drawerService.openUserGroupDrawer();
    }
  }

  onConfigurationClick(configUuid: string): void {
    this.configurationClick.emit(configUuid);    
    const config = this.configCache.get(configUuid) || 
                 (this.dataSource && this.dataSource.data ? 
                  this.dataSource.data.find(c => c.configurationId === configUuid) : null);
    
    if (config) {
      this.dataService.setCurrentSelectedConfigurationDetails(configUuid);      
      this.drawerService.openConfigurationDrawer();
    }
  }
  
  getUserNameById(userId: number): string {    
    if (this.userCache.has(userId)) {
      return this.userCache.get(userId).name;
    }
    const userFromArray = this.users.find(u => u.userId === userId);
    if (userFromArray) {
      this.userCache.set(userId, userFromArray);
      return userFromArray.name;
    }    
    return `User ${userId}`;
  }
  
  getUserGroupNameById(userGroupId: number): string {    
    if (this.userGroupCache.has(userGroupId)) {
      return this.userGroupCache.get(userGroupId).name;
    }    
    const groupFromArray = this.userGroups.find(g => g.id === userGroupId);
    if (groupFromArray) {
      this.userGroupCache.set(userGroupId, groupFromArray);
      return groupFromArray.name;
    }
    return `Group ${userGroupId}`;
  }

  updateConfigurationGroups() {
    if (!this.dataSource || !this.dataSource.data) {
      return;
    }

    this.dataSource.data.forEach(config => {
      config['inAccessGroups'] = this.accessGroups.filter(group =>
        group.configurationUuids && group.configurationUuids.includes(config.configurationId)
      );
    });
  }

  openAddAccessGroupDialog(groupUuid?: string): void {
    const qraDefaultGroup = this.accessGroups.find(group => 
      group.isDefault || group.name === 'QRA Default'
    );
    
    const qraDefaultConfigUuids = qraDefaultGroup ? 
      qraDefaultGroup.configurationUuids || [] : [];
  
    const dialogRef = this.dialog.open(AddAccessGroupDialogComponent, {
      width: '500px',
      data: {
        isEdit: !!groupUuid,
        groupUuid: groupUuid,
        qraDefaultConfigUuids: qraDefaultConfigUuids
      }
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.accessGroupEvent.emit({
          action: groupUuid ? 'edit' : 'add',
          result: result
        });
  
        this.loadAccessGroupsRequest.emit();
      }
    });
  }
  
  editAccessGroup(groupUuid: string): void {
    this.openAddAccessGroupDialog(groupUuid);
  }

  deleteAccessGroup(groupUuid: string): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Access Group',
        content: 'Are you sure you want to delete this access group?',
        confirmText: 'Delete ',
        rejectText: 'Cancel'
      }
    });
  
    const componentInstance = dialogRef.componentInstance;
    componentInstance.confirmation.subscribe(() => {
      this.deleteAccessGroupRequest.emit(groupUuid);      
      this.accessGroupEvent.emit({
        action: 'delete',
        result: { uuid: groupUuid }
      });
    });
  }

  toggleGroupExpansion(group: GroupWithAssociations, expanded: boolean): void {
    group.expanded = expanded;
  }

  getConfigNameByUuid(uuid: string): string {
    if (this.dataSource && this.dataSource.data && this.dataSource.data.length > 0) {
      const configFromDS = this.dataSource.data.find(c => c.configurationId === uuid);
      if (configFromDS) {
        this.configCache.set(uuid, configFromDS);
        return configFromDS.configurationName;
      }
    }    
    if (this.configCache.has(uuid)) {
      return this.configCache.get(uuid).configurationName;
    }    
    if ((!this.dataSource || !this.dataSource.data || this.dataSource.data.length === 0) && 
        !this.configCache.size) {
      this.refreshEmitter.emit();
      return 'Loading...';
    }
    
    return uuid;
  }

  getConfigVersionByUuid(uuid: string): string {    
    if (this.dataSource && this.dataSource.data) {
      const configFromDS = this.dataSource.data.find(c => c.configurationId === uuid);
      if (configFromDS) {
        this.configCache.set(uuid, configFromDS);
        return configFromDS.configurationVersion.toString();
      }
    }    
    if (this.configCache.has(uuid)) {
      return this.configCache.get(uuid).configurationVersion.toString();
    }
    
    return '';
  }

  refresh(): void {
    this.configCache.clear();
    this.userCache.clear();
    this.userGroupCache.clear();
    this.refreshEmitter.emit();
  }
}